import { marked } from "marked";

type Html2PdfChain = {
  set: (opt: Record<string, unknown>) => {
    from: (el: HTMLElement) => {
      save: () => Promise<void>;
    };
  };
};

type Html2PdfLoader = ((opt?: unknown) => Html2PdfChain) & {
  default?: (opt?: unknown) => Html2PdfChain;
};

export async function exportResultToPDF(
  markdownContent: string,
  title: string
): Promise<void> {
  const mod = (await import("html2pdf.js")) as unknown as Html2PdfLoader;
  const html2pdf =
    typeof mod.default === "function"
      ? mod.default
      : (mod as unknown as (opt?: unknown) => Html2PdfChain);

  const container = document.createElement("div");
  container.style.fontFamily = "Helvetica, Arial, sans-serif";
  container.style.lineHeight = "1.5";
  container.style.fontSize = "12px";
  container.style.color = "#111827";

  const style = document.createElement("style");
  style.textContent = `
    *{box-sizing:border-box}
    h1{font-size:20px;font-weight:700;margin:0 0 10px}
    h2{font-size:18px;font-weight:700;margin:12px 0 8px}
    h3{font-size:16px;font-weight:700;margin:10px 0 6px}
    p{margin:0 0 8px; padding-bottom:2px; break-inside: avoid-page; page-break-inside: avoid}
    ul{margin:0 0 8px 18px; list-style-type: disc; padding-left: 22px}
    /* Top-level ordered list numbering only */
    .md-root > ol{margin:0 0 8px 28px; list-style: none; padding-left: 0; counter-reset: topitem}
    .md-root > ol > li{margin:4px 0; padding-bottom:2px; position: relative; counter-increment: topitem}
    .md-root > ol > li::before{content: counter(topitem) '. '; position: absolute; left: -28px; width: 24px; text-align: right; font-weight: 600}
    /* Nested lists keep native markers */
    .md-root ol ol{list-style: decimal; padding-left: 24px}
    .md-root ol ul{list-style: disc; padding-left: 22px}
    li{margin:4px 0; break-inside: avoid-page; page-break-inside: avoid}
    table{border-collapse:collapse;width:100%;margin:10px 0}
    th,td{border:1px solid #e5e7eb;padding:6px 8px;text-align:left}
    code{font-family:Consolas,'Courier New',monospace;background:#f3f4f6;padding:1px 3px;border-radius:3px}
    pre{background:#f3f4f6;padding:8px;border-radius:6px;overflow:auto}
    /* Page break helpers (modern + legacy) */
    h1,h2,h3{break-inside: avoid-page; page-break-inside: avoid; page-break-after: avoid}
    table,pre,blockquote{break-inside: avoid-page; page-break-inside: avoid}
  `;
  container.appendChild(style);

  const titleEl = document.createElement("h1");
  titleEl.textContent = title;
  container.appendChild(titleEl);

  const html = marked.parse(markdownContent || "") as string;
  const contentEl = document.createElement("div");
  contentEl.className = "md-root";
  contentEl.innerHTML = html;

  try {
    const categoryRegex = /^(Kognitif|Afektif|Psikomotorik)\s*:/i;
    const topOls = Array.from(
      contentEl.querySelectorAll(":scope > ol")
    ) as HTMLOListElement[];

    topOls.forEach(ol => {
      const items = Array.from(ol.children).filter(
        n => n.tagName === "LI"
      ) as HTMLLIElement[];
      for (let i = 0; i < items.length; i++) {
        const li = items[i];
        const text = (li.textContent || "").trim();
        if (categoryRegex.test(text)) {
          // Create a nested list if not already present
          let nested = li.querySelector("ol, ul") as
            | HTMLOListElement
            | HTMLUListElement
            | null;
          if (!nested) {
            nested = document.createElement("ol");
            li.appendChild(nested);
          }
          // Move following lis that are NOT category headers into nested list
          while (i + 1 < items.length) {
            const nextLi = items[i + 1];
            const nextText = (nextLi.textContent || "").trim();
            if (categoryRegex.test(nextText)) break;
            nested.appendChild(nextLi);
            items.splice(i + 1, 1);
          }
        }
      }
    });
  } catch {
    void 0;
  }

  try {
    const pxPerPt = 96 / 72; // CSS px per PDF point
    const pageHeightPt = 842; // A4 portrait height in pt
    const margins = [12, 12, 18, 12]; // must stay in sync with opt.margin
    const pageHeightPx = pageHeightPt * pxPerPt;
    const usableHeightPx = pageHeightPx - (margins[0] + margins[2]) * pxPerPt;
    const minBottomClearancePx = 140;

    const candidates = contentEl.querySelectorAll("h1, h2, h3, p, li");
    candidates.forEach(el => {
      let y = 0 as number;
      let n: HTMLElement | null = el as HTMLElement;
      while (n && n !== contentEl) {
        y += n.offsetTop;
        n = n.offsetParent as HTMLElement | null;
      }
      const elHeight = (el as HTMLElement).offsetHeight || 0;
      const yOnPage = y % pageHeightPx;
      const bottomOnPage = yOnPage + elHeight;
      if (
        bottomOnPage > usableHeightPx ||
        yOnPage > usableHeightPx - minBottomClearancePx
      ) {
        const br = document.createElement("div");
        br.className = "html2pdf__page-break";
        el.parentNode?.insertBefore(br, el);
      }
    });
  } catch {
    // Ignore errors in page break calculation
  }

  container.appendChild(contentEl);

  const opt: Record<string, unknown> = {
    margin: [12, 12, 20, 12],
    filename: `${(title || "Generated_Content").replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 1.5, useCORS: true, backgroundColor: "#ffffff" },
    jsPDF: { unit: "pt", format: "a4", orientation: "portrait" },
    pagebreak: {
      mode: ["css", "legacy"],
      avoid: ["p", "li", "ul", "h1", "h2", "h3", "table", "pre", "blockquote"],
    },
  };

  await html2pdf().set(opt).from(container).save();
}
